import styled from 'styled-components';
import {
  Select,
  FormControl,
  MenuItem,
  Typography,
  FormHelperText,
  useTheme,
} from '@mui/material';
import { Field } from 'react-final-form';
import RiskScale from './RiskScale';
import setColors from 'lib/utils/setColors';
import { useTranslation } from 'react-i18next';
import { Input } from 'components/ui';

const FormRiskSelect = (props) => {
  const {
    name,
    initialValue,
    isDecimal,
    displayRisk, // Keep for backward compatibility
    scoreMode, // New explicit prop: 'RISK' | 'WELLNESS' | 'NEUTRAL'
    includeNoScoreOption = false,
    disabled = false,
    onChange,
    'data-testid': dataTestId,
    ...elseProps
  } = props;

  const theme = useTheme();
  
  // Determine the actual mode with backward compatibility
  const actualMode = scoreMode || 
    (displayRisk === true ? 'RISK' : 
     displayRisk === false ? 'WELLNESS' : 
     'NEUTRAL');
  
  const options = actualMode === 'RISK'
    ? [...(includeNoScoreOption ? ['no_score'] : []), 0, 1, 2, 3, 4, 5]
    : [
        ...(includeNoScoreOption ? ['no_score'] : []),
        ...Array.from(Array(31).keys()),
      ];
  const { t } = useTranslation();
  
  return (
    <SContainer {...elseProps}>
      <Typography marginRight="15px">{t('Score')}</Typography>
      <Field name={name}>
        {({ input, meta }) => {
          // 3-state logic with explicit modes
          if (actualMode === 'RISK') {
            // RISK Mode: Dropdown 0-5 with colors
            return (
              <FormControl sx={{ width: 'fit-content', m: '10px 0' }} fullWidth>
              <SMuiSelect
                {...input}
                value={
                  input.value === ''
                    ? 'no_score'
                    : isDecimal
                    ? parseFloat(input.value)
                    : input.value
                }
                data-id="risk-select"
                inputProps={{
                  'data-testid': dataTestId
                }}
                labelColor={
                  !displayRisk
                    ? 'transparent'
                    : setColors(
                        isDecimal ? parseFloat(input.value) : input.value,
                        theme
                      )?.color
                }
                renderValue={(value) => {
                  return value === 'no_score' ? t('No score') : value;
                }}
                label={false}
                options={options}
                error={meta.touched && !!meta.error}
                disabled={disabled}
                onChange={(e) => {
                  input.onChange(e);
                  onChange?.(e?.target?.value);
                }}
              >
                {options.map((item, idx) => (
                  <SMuiMenuItem
                    value={item}
                    selected={
                      isDecimal ? parseFloat(input.value) : input.value === item
                    }
                  >
                    <p>{item === 'no_score' ? t('No score') : item}</p>
                    {actualMode === 'RISK' && <RiskScale risk={item} />}
                  </SMuiMenuItem>
                ))}
              </SMuiSelect>
              {meta.touched && !!meta.error && (
                <FormHelperText error>{meta.error}</FormHelperText>
              )}
            </FormControl>
            );
          } else if (actualMode === 'WELLNESS') {
            // WELLNESS Mode: Input 0-100 with %
            return (
            <SWellnessInputContainer>
              <Input
                {...input}
                value={input.value === '' ? '' : input.value}
                type="number"
                placeholder="0 to 100"
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  min: 0,
                  max: 100,
                  'data-testid': dataTestId
                }}
                sx={{ minWidth: '100px' }}
                disabled={disabled}
                onKeyDown={(evt) => {
                  if (['e', 'E', '+', '.', ',', '-'].includes(evt.key)) {
                    evt.preventDefault();
                  }
                }}
                onChange={(e) => {
                  let value = e?.target?.value?.replace(/[eE+.,\-]/g, '');
                  // Constrain to 0-100 range for WELLNESS scores
                  if (value !== '') {
                    const numValue = parseInt(value);
                    if (numValue > 100) value = '100';
                    if (numValue < 0) value = '0';
                  }
                  onChange?.(value);
                  input.onChange(value);
                }}
              />
              <SPercentageSymbol>%</SPercentageSymbol>
              {meta.touched && !!meta.error && (
                <FormHelperText error>{meta.error}</FormHelperText>
              )}
            </SWellnessInputContainer>
            );
          } else {
            // NEUTRAL Mode: Free numeric input with no limitations
            return (
              <FormControl sx={{ width: 'fit-content', m: '10px 0' }}>
                <Input
                  {...input}
                  value={input.value === '' ? '' : input.value}
                  type="number"
                  placeholder="Score"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    'data-testid': dataTestId
                  }}
                  sx={{
                    minWidth: '80px',
                    maxWidth: '120px',
                    width: 'fit-content'
                  }}
                  disabled={disabled}
                  onKeyDown={(evt) => {
                    if (['e', 'E', '+', '.', ',', '-'].includes(evt.key)) {
                      evt.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    let value = e?.target?.value?.replace(/[eE+.,\-]/g, '');
                    onChange?.(value);
                    input.onChange(value);
                  }}
                  error={meta.touched && !!meta.error}
                />
                {meta.touched && !!meta.error && (
                  <FormHelperText error>{meta.error}</FormHelperText>
                )}
              </FormControl>
            );
          }
        }}
      </Field>
    </SContainer>
  );
};

export default FormRiskSelect;

const SContainer = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
`;

const SMuiSelect = styled(Select)`
  && {
    min-width: 86px;
    height: 26px;
    font-size: 14px;
    .MuiOutlinedInput-input {
      padding: 0 13px;
    }

    p {
      position: absolute;
      top: 2px;
    }

    img {
      display: none !important;
    }

    ::before {
      content: '';
      position: absolute;
      left: 5px;
      width: 2px;
      height: 16px;
      background-color: ${({ labelColor }) =>
        labelColor ? labelColor : 'inherit'};
    }

    fieldset {
      top: 0;
      legend {
        display: none;
      }
    }
  }
`;
const SMuiMenuItem = styled(MenuItem)`
  && {
    height: 26px;
    justify-content: space-between;
  }
`;

const SWellnessInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SPercentageSymbol = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.primary};
  user-select: none;
`;
